import { z } from 'zod/v4';

/**
 * Sort columns the admin companies list endpoint accepts. Each key maps to a
 * SQL expression in `core/db/companies/list-admin.ts` (`SORT_COLUMNS`) — keep
 * the two in sync when adding/removing columns.
 */
export const AdminCompaniesListSortColumns = [
  'updatedAt',
  'createdAt',
  'name',
  'country',
  'officialName',
  'city',
  'collaborationDateStart',
  'connectionType',
  'collaborationStatus',
  'facilityProcesses',
  'riskScore',
  'supplyChainTier',
  'collaborationEndDate',
  'responsiblePerson',
  'vendors',
  'agents',
  'allowSharingAddress',
  'allowSharingProof',
  'contactPersonEmail',
  'enabledCustomInfo',
  'internalCompanyCode',
  'notes',
  'activeUsersCount',
  'companySize',
  'companyType',
  'founded',
  'isDemoCompany',
  'isClientCompany',
  'relationshipType',
] as const;

export type AdminCompaniesListSortColumn =
  (typeof AdminCompaniesListSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

export const AdminCompaniesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  filter: z.string().max(512).optional(),
  hasFeature: z.string().min(1).optional(),
  isDropDown: booleanLike,
  /** true = only soft-removed companies, false/absent = only active companies. */
  removed: booleanLike,
});

export type AdminCompaniesListQuery = z.infer<
  typeof AdminCompaniesListQuerySchema
>;

/**
 * Row shape returned by the admin companies list. `looseObject` because the
 * SQL returns ~40 columns and strictly typing all of them here duplicates
 * `backend/src/types/companies` without buying validation strength for an
 * admin-only read endpoint — but the fields a consumer actually reads (see
 * `core/db/companies/types.ts`'s `RawAdminCompanyListRow` and
 * `list-admin.ts`'s `transformRow` for the verified wire shape of each) are
 * typed strictly below, so frontend code can `z.infer` this instead of
 * hand-rolling a parallel type that silently drifts from the real response.
 */
export const AdminCompanyListRowSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullable().optional(),
  officialName: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  parentId: z.string().nullable().optional(),
  parentDisplayName: z.string().nullable().optional(),
  byCompanyId: z.string().nullable().optional(),
  /** Comma-joined facility-process ids (not an array) — see `transformRow`. */
  facilityProcesses: z.string().nullable().optional(),
  activeUsersCount: z.union([z.number(), z.string()]).nullable().optional(),
  totalUsersCount: z.union([z.number(), z.string()]).nullable().optional(),
  /** `0`/`1`, as `companies.is_demo_company` stores it — not a boolean. */
  isDemoCompany: z.number().nullable().optional(),
  isClientCompany: z.number().nullable().optional(),
  /** A genuine `Date` at runtime (confirmed via the live serializer, not
   *  `RawAdminCompanyListRow`'s `string | null` annotation, which turned out
   *  to be inaccurate) — coerced the same way `companySchema`'s date fields
   *  are, and JSON-serializes to a string on the wire either way. */
  updatedAt: z.coerce.date().nullable().optional(),
  /** First image of the About profile's `image_list`, resolved in `transformRow`. */
  featuredImageFileUrl: z.string().nullable().optional(),
});

export type AdminCompanyListRow = z.infer<typeof AdminCompanyListRowSchema>;

// The envelope (metadata.pagination + data) is applied at the route via
// buildResponseSchemas({ paginated: true }) — this is just the row array.
export const AdminCompaniesListResponseSchema = z.array(
  AdminCompanyListRowSchema,
);

export type AdminCompaniesListResponse = z.infer<
  typeof AdminCompaniesListResponseSchema
>;
